import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { ArrowLeft, Plus, Trash2, Save, Map as MapIcon, Coins, Layers } from "lucide-react";
import { toast } from "sonner";

const emptyPricing = { standard_tiers: [], large_tiers: [], half_pallet_price: null };

const TierEditor = ({ title, tiers, onChange, testidPrefix }) => {
  const set = (i, key, val) => {
    const next = tiers.map((t, idx) => (idx === i ? { ...t, [key]: val } : t));
    onChange(next);
  };
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[var(--text-3)] mb-2">{title}</div>
      <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-[var(--text-3)] pb-1">
        <div className="col-span-4">Ab Paletten</div>
        <div className="col-span-6">CHF / Palette</div>
        <div className="col-span-2"></div>
      </div>
      {tiers.length === 0 && <div className="text-xs text-[var(--text-3)] py-2">Keine Stufen erfasst.</div>}
      {tiers.map((t, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center pb-1.5" data-testid={`${testidPrefix}-row-${i}`}>
          <input
            type="number" min={1} step={1}
            className="input-base text-sm font-mono col-span-4"
            value={t.min_qty}
            onChange={(e) => set(i, "min_qty", Number(e.target.value || 1))}
            data-testid={`${testidPrefix}-minqty-${i}`}
          />
          <input
            type="number" min={0} step="0.01"
            className="input-base text-sm font-mono col-span-6"
            value={t.price_per_pallet}
            onChange={(e) => set(i, "price_per_pallet", Number(e.target.value || 0))}
            data-testid={`${testidPrefix}-price-${i}`}
          />
          <button
            type="button"
            onClick={() => onChange(tiers.filter((_, idx) => idx !== i))}
            data-testid={`${testidPrefix}-del-${i}`}
            className="col-span-2 text-[var(--text-3)] hover:text-red-700 flex justify-center"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...tiers, { min_qty: tiers.length ? Math.max(...tiers.map(t => t.min_qty)) + 1 : 1, price_per_pallet: 0 }])}
        data-testid={`${testidPrefix}-add`}
        className="mt-1 text-[11px] uppercase tracking-wider text-[#D4AF37] hover:text-[var(--text)] flex items-center gap-1"
      >
        <Plus size={12} /> Stufe hinzufügen
      </button>
    </div>
  );
};

export default function DriverPayouts() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [draft, setDraft] = useState({ name: "", from_city: "", from_zip: "", to_city: "", to_zip: "", payout_chf: 0 });
  const [pricing, setPricing] = useState(emptyPricing);
  const [savingPricing, setSavingPricing] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const [d, rs, pr] = await Promise.all([
        api.get("/drivers").then(r => (r.data || []).find(x => x.id === id)),
        api.get(`/drivers/${id}/routes`).then(r => r.data),
        api.get(`/drivers/${id}/pricing`).then(r => r.data).catch(() => emptyPricing),
      ]);
      setDriver(d);
      setRoutes(rs);
      setPricing({
        standard_tiers: pr.standard_tiers || [],
        large_tiers: pr.large_tiers || [],
        half_pallet_price: pr.half_pallet_price ?? null,
      });
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!draft.name || !(draft.payout_chf > 0)) { setError("Name und Betrag erforderlich"); return; }
    try {
      await api.post(`/drivers/${id}/routes`, draft);
      setDraft({ name: "", from_city: "", from_zip: "", to_city: "", to_zip: "", payout_chf: 0 });
      setError("");
      load();
    } catch (e) { setError(formatApiError(e)); }
  };
  const del = async (rid) => {
    if (!window.confirm("Strecke löschen?")) return;
    await api.delete(`/drivers/${id}/routes/${rid}`);
    load();
  };

  const savePricing = async () => {
    setSavingPricing(true); setError("");
    try {
      const payload = {
        ...pricing,
        half_pallet_price: pricing.half_pallet_price === "" || pricing.half_pallet_price == null ? null : Number(pricing.half_pallet_price),
      };
      await api.put(`/drivers/${id}/pricing`, payload);
      toast.success("Staffelpreise gespeichert", { description: driver?.name });
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error("Speichern fehlgeschlagen", { description: msg });
    } finally { setSavingPricing(false); }
  };

  if (loading) return <Layout><div className="p-10 text-center text-[var(--text-3)]">Lade…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-6">
        <header>
          <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-wider text-[var(--text-3)] hover:text-[#D4AF37] flex items-center gap-1 mb-2"><ArrowLeft size={12} /> Zurück</button>
          <div className="label-eyebrow" style={{ color: "var(--gold)" }}>Fahrer-Pauschalen</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">{driver?.name || "Fahrer"}</h1>
          <p className="text-sm text-[var(--text-2)] mt-1">Vorerfasste Strecken mit Pauschalbetrag. Beim Auftrags-Zuweisen wählt der Admin die Strecke per Dropdown — Betrag wird automatisch eingetragen, kann pro Auftrag überschrieben werden.</p>
        </header>

        {error && <div className="border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm">{error}</div>}

        <div className="card p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-sm" style={{ background: "var(--accent-muted)" }}>
              <MapIcon size={16} strokeWidth={1.5} style={{ color: "var(--header)" }} />
            </div>
            <h2 className="text-base font-semibold">Strecken & Pauschalbeträge</h2>
          </div>

          <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wider text-[var(--text-3)] pb-2">
            <div className="col-span-3">Name</div>
            <div className="col-span-2">PLZ Von</div>
            <div className="col-span-2">PLZ Nach</div>
            <div className="col-span-2">CHF</div>
            <div className="col-span-3 text-right">Aktion</div>
          </div>
          {routes.length === 0 && <div className="text-sm text-[var(--text-3)] text-center py-4">Noch keine Strecken erfasst.</div>}
          {routes.map(r => (
            <div key={r.id} className="grid grid-cols-12 gap-2 items-center pb-2 border-b border-[var(--border)]" data-testid={`driver-route-row-${r.id}`}>
              <div className="col-span-3 text-sm">{r.name}</div>
              <div className="col-span-2 text-xs text-[var(--text-2)]">{r.from_zip} {r.from_city}</div>
              <div className="col-span-2 text-xs text-[var(--text-2)]">{r.to_zip} {r.to_city}</div>
              <div className="col-span-2 font-mono text-sm" style={{ color: "var(--header)" }}>{Number(r.payout_chf).toFixed(2)}</div>
              <div className="col-span-3 text-right">
                <button onClick={() => del(r.id)} data-testid={`driver-route-del-${r.id}`} className="text-[var(--text-3)] hover:text-red-700"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          <div className="grid grid-cols-12 gap-2 items-center pt-4 mt-2 border-t border-[var(--border)]">
            <input className="input-base text-sm col-span-3" placeholder="z.B. ZH → BE" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} data-testid="driver-route-name" />
            <input className="input-base text-sm col-span-2 font-mono" placeholder="8000" value={draft.from_zip} onChange={e => setDraft({ ...draft, from_zip: e.target.value })} data-testid="driver-route-from-zip" />
            <input className="input-base text-sm col-span-2 font-mono" placeholder="3000" value={draft.to_zip} onChange={e => setDraft({ ...draft, to_zip: e.target.value })} data-testid="driver-route-to-zip" />
            <input type="number" min={0} step="0.01" className="input-base text-sm col-span-2 font-mono" value={draft.payout_chf} onChange={e => setDraft({ ...draft, payout_chf: Number(e.target.value || 0) })} data-testid="driver-route-payout" />
            <button onClick={add} data-testid="driver-route-add" className="col-span-3 btn-gold text-xs uppercase tracking-wider py-2 rounded-sm flex items-center justify-center gap-1"><Plus size={12} /> Hinzufügen</button>
          </div>
        </div>

        {/* Staffelpreise */}
        <div className="card p-5" data-testid="driver-tier-pricing-card">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-sm" style={{ background: "var(--accent-muted)" }}>
                <Layers size={16} strokeWidth={1.5} style={{ color: "var(--header)" }} />
              </div>
              <h2 className="text-base font-semibold">Staffelpreise (pro Palette)</h2>
            </div>
          </div>
          <p className="text-xs text-[var(--text-3)] mb-4">
            Vergütung pro Palette nach Menge (z.B. ab 3 Paletten → tieferer Stückpreis). Im Auftrag kann der Admin
            mit „Nach Staffel berechnen" den Betrag automatisch vorschlagen lassen — überschreibbar pro Auftrag.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <TierEditor
              title="Standardpaletten (120×80)"
              tiers={pricing.standard_tiers}
              onChange={(t) => setPricing(p => ({ ...p, standard_tiers: t }))}
              testidPrefix="tier-std"
            />
            <TierEditor
              title="Grosspaletten (160×80)"
              tiers={pricing.large_tiers}
              onChange={(t) => setPricing(p => ({ ...p, large_tiers: t }))}
              testidPrefix="tier-lg"
            />
          </div>

          <div className="mt-5 pt-4 border-t border-[var(--border)] grid md:grid-cols-2 gap-4 items-end">
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-3)] block mb-2">½ Palette (CHF pro Stück)</label>
              <input
                type="number" min={0} step="0.01"
                className="input-base text-sm font-mono"
                placeholder="z.B. 30.00"
                value={pricing.half_pallet_price ?? ""}
                onChange={(e) => setPricing(p => ({ ...p, half_pallet_price: e.target.value === "" ? null : Number(e.target.value) }))}
                data-testid="tier-half-price"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={savePricing}
                disabled={savingPricing}
                data-testid="tier-pricing-save"
                className="btn-gold px-5 py-2.5 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2"
              >
                <Save size={13} /> {savingPricing ? "Speichern…" : "Staffelpreise speichern"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
